import React, { useState, useEffect, useRef } from 'react';
import GameSplashScreen from './GameSplashScreen';
import QuizCodeEntry from './QuizCodeEntry';
import ChallengeFriendsEntry from './ChallengeFriendsEntry';

const GameWrapper = ({ current_user, quizCode }) => {
  const autoStartedRef = useRef(false);
  const [gameState, setGameState] = useState('loading'); // 'splash', 'quiz_code_entry', 'loading', 'playing'
  const [selectedMode, setSelectedMode] = useState(quizCode ? 'teacher_mode' : 'career');
  const [quizCodeState, setQuizCodeState] = useState(quizCode || null);
  const [phaserLoaded, setPhaserLoaded] = useState(false);
  const [JumpingGameComponent, setJumpingGameComponent] = useState(null);

  // The default path should feel like a mobile app: tap Brain Climb and start playing.
  useEffect(() => {
    if (autoStartedRef.current) return;

    autoStartedRef.current = true;

    if (quizCode) {
      setSelectedMode('teacher_mode');
      setQuizCodeState(quizCode);
      loadGameAssets('teacher_mode');
    } else {
      loadGameAssets('career');
    }
  }, [quizCode]);

  // Lazy load Phaser.js and the JumpingGame component
  const loadGameAssets = async (mode) => {
    setGameState('loading');
    setSelectedMode(mode);

    try {
      // Load Phaser.js dynamically and make it globally available
      if (!window.Phaser) {
        const PhaserModule = await import('phaser');
        // Make Phaser available globally for the GameScene class
        window.Phaser = PhaserModule.default || PhaserModule;
      }
      setPhaserLoaded(true);

      // Load the appropriate game component based on mode
      if (mode === 'career') {
        // Load the original full JumpingGame for career mode
        const { default: OriginalJumpingGame } = await import('./JumpingGame');
        setJumpingGameComponent(() => OriginalJumpingGame);
      } else if (mode === 'teacher_mode' || mode === 'challenge_friends') {
        // Load the full JumpingGame for quiz mode (teacher or friends)
        const { default: JumpingGame } = await import('./JumpingGame');
        setJumpingGameComponent(() => JumpingGame);
      } else {
        // For other modes (when they're implemented), use the lazy version
        const { default: JumpingGameLazy } = await import('./JumpingGameLazy');
        setJumpingGameComponent(() => JumpingGameLazy);
      }

      // Small delay to show loading state
      setTimeout(() => {
        setGameState('playing');
      }, 500);

    } catch (error) {
      console.error('❌ Failed to load game assets:', error);
      // Fallback to splash screen
      setGameState('splash');
    }
  };

  const handleStartGame = (mode) => {
    if (mode === 'career') {
      loadGameAssets(mode);
    } else if (mode === 'teacher_mode' || mode === 'challenge_friends') {
      setGameState('quiz_code_entry');
      setSelectedMode(mode);
    } else {
      // For coming soon modes, just show a message
    }
  };

  const handleStartQuizGame = (code) => {
    setQuizCodeState(code);
    loadGameAssets(selectedMode);
  };

  const handleOpenTeacherCode = () => {
    setSelectedMode('teacher_mode');
    setGameState('quiz_code_entry');
  };

  const handleBackToCareer = () => {
    setSelectedMode('career');
    setQuizCodeState(null);
    loadGameAssets('career');
  };

  // Render based on current state
  if (gameState === 'splash') {
    return (
      <GameSplashScreen
        onStartGame={handleStartGame}
        currentUser={current_user}
      />
    );
  }

  if (gameState === 'quiz_code_entry') {
    // Use different components based on the selected mode
    if (selectedMode === 'challenge_friends') {
      return (
        <ChallengeFriendsEntry
          onStartQuizGame={handleStartQuizGame}
          onBack={handleBackToCareer}
          currentUser={current_user}
        />
      );
    } else {
      // Teacher mode uses the original component
      return (
        <QuizCodeEntry
          onStartQuizGame={handleStartQuizGame}
          onBack={handleBackToCareer}
        />
      );
    }
  }

  if (gameState === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-white border-t-transparent mx-auto mb-6"></div>
          <h2 className="text-3xl font-bold text-white mb-4">Loading Game Engine...</h2>
          <p className="text-gray-300 mb-2">
            {selectedMode === 'teacher_mode' ? 'Loading your teacher challenge' : 'Starting your career climb'}
          </p>
          <div className="w-64 bg-gray-700 rounded-full h-2 mx-auto">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full animate-pulse" style={{width: '70%'}}></div>
          </div>
        </div>
      </div>
    );
  }

  if (gameState === 'playing' && JumpingGameComponent) {
    return (
      <div className="relative">
        <div className="absolute top-4 left-4 z-50 flex gap-2">
          {selectedMode === 'teacher_mode' ? (
            <button
              onClick={handleBackToCareer}
              className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 text-xs sm:text-sm font-bold"
            >
              Career Climb
            </button>
          ) : (
            <button
              onClick={handleOpenTeacherCode}
              className="bg-amber-500 bg-opacity-90 hover:bg-opacity-100 text-slate-950 px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 font-bold shadow-lg text-xs sm:text-sm"
            >
              <span className="sm:hidden">Teacher code</span>
              <span className="hidden sm:inline">Have a teacher code?</span>
            </button>
          )}
        </div>

        {/* The actual game component */}
        <JumpingGameComponent
          current_user={current_user}
          gameMode={selectedMode}
          quizCode={quizCodeState}
        />
      </div>
    );
  }

  // Fallback loading state
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mx-auto mb-4"></div>
        <h2 className="text-2xl font-bold text-white">Loading...</h2>
      </div>
    </div>
  );
};

export default GameWrapper;
